// packages/web-ui/src/AppShell/drawer-tabs/get-default-drawer-tab.ts
//
// W2-S9 — pure helper that picks the canonical drawer tab for a given
// destination (UX §7.3 line 1031–1046).
//
//   chat / diff           → 'activity'
//   kb / files / views    → 'detail'
//   settings              → lastUsedByDestination['settings'] ?? 'activity'
//
// The helper is kept pure (no zustand / no DOM) so it is unit-testable in
// isolation. Callers (`<UtilityDrawer>`, the AppShell destination-change
// effect) supply `lastUsedByDestination` from the slice.

import type { ActiveDestination } from '../../state/navigation.js';
import type {
  DrawerTab,
  LastUsedByDestination,
} from '../../state/utilityDrawer.js';

/**
 * Maps a destination to its canonical drawer tab, honouring the user's
 * last-used tab when the destination has no documented default
 * (currently only `settings` per UX §7.3).
 */
export function getDefaultDrawerTab(
  destination: ActiveDestination,
  lastUsedByDestination: LastUsedByDestination,
): DrawerTab {
  switch (destination) {
    case 'chat':
    case 'diff':
    case 'git':
    case 'skills':
      return 'activity';
    case 'kb':
    case 'files':
    case 'views':
      return 'detail';
    case 'settings':
      return lastUsedByDestination[destination] ?? 'activity';
    default: {
      // Exhaustiveness — TypeScript narrows `destination` to `never` here.
      // If a new ActiveDestination ever lands without a case, fall back to
      // 'activity' rather than throwing — the drawer must always render.
      const _exhaustive: never = destination;
      void _exhaustive;
      return 'activity';
    }
  }
}
